import type { GatewayBrowserClient } from "../gateway.ts";
import type { SkillStatusEntry, SkillStatusReport } from "../types.ts";

/** Minimal shape returned by igloobot.skills.list for each DB skill. */
type DbSkillEntry = {
  id: string;
  name: string;
  description: string;
  content: string;
  isActive: boolean;
  projectId: string | null;
  metadata: Record<string, unknown>;
  version: number;
};

type DbSkillsListResult = {
  ok: boolean;
  skills?: DbSkillEntry[];
  error?: string;
};

export type AgentSkillsState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  agentSkillsLoading: boolean;
  agentSkillsError: string | null;
  agentSkillsReport: SkillStatusReport | null;
  agentSkillsAgentId: string | null;
};

/**
 * Load skills for a specific agent by fetching both the DB-backed Skills Server
 * list (igloobot.skills.list — source of truth) and the file-based skills status
 * (skills.status — for eligibility metadata) in parallel, then merging them.
 *
 * The DB `isActive` field becomes `globalActive` on each entry.
 * The DB skill list is the authoritative set — no extra skills are added from
 * file-based sources. File-based metadata (source, eligible, requirements) is
 * overlaid onto matching DB skills for richer display.
 */
export async function loadAgentSkills(state: AgentSkillsState, agentId: string) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.agentSkillsLoading) {
    return;
  }
  state.agentSkillsLoading = true;
  state.agentSkillsError = null;
  try {
    // Fetch both in parallel
    const [dbRes, fileRes] = await Promise.all([
      state.client
        .request<DbSkillsListResult>("igloobot.skills.list", { activeOnly: false })
        .catch(() => null),
      state.client.request<SkillStatusReport>("skills.status", { agentId }).catch(() => null),
    ]);

    const dbSkills = dbRes?.skills ?? [];
    const fileSkills = fileRes?.skills ?? [];
    const merged = mergeSkills(dbSkills, fileSkills);

    state.agentSkillsReport = {
      workspaceDir: fileRes?.workspaceDir ?? "",
      managedSkillsDir: fileRes?.managedSkillsDir ?? "",
      skills: merged,
    };
    state.agentSkillsAgentId = agentId;
  } catch (err) {
    state.agentSkillsError = String(err);
  } finally {
    state.agentSkillsLoading = false;
  }
}

/**
 * Merge DB skills (source of truth) with file-based skill metadata.
 *
 * The DB skill list is the authoritative set. For each DB skill:
 *   - If a matching file-based skill exists (by name, case-insensitive),
 *     the file-based metadata (source, eligible, requirements, etc.) is used
 *     for richer display, with `globalActive` set from DB `isActive`.
 *   - If no file-based match, a synthetic SkillStatusEntry is created
 *     with source "igloobot-db".
 *
 * File-only skills (not in DB) are NOT included — the DB is the source of truth.
 */
function mergeSkills(dbSkills: DbSkillEntry[], fileSkills: SkillStatusEntry[]): SkillStatusEntry[] {
  // Build a lookup from file-based skills by lowercase name
  const fileByName = new Map<string, SkillStatusEntry>();
  for (const file of fileSkills) {
    fileByName.set(file.name.toLowerCase(), file);
  }

  const merged: SkillStatusEntry[] = [];

  for (const db of dbSkills) {
    const key = db.name.toLowerCase();
    const file = fileByName.get(key);

    if (file) {
      // Enrich file-based entry with DB authority
      merged.push({
        ...file,
        globalActive: db.isActive,
      });
    } else {
      // DB-only skill — create synthetic entry
      const meta = db.metadata ?? {};
      merged.push({
        name: db.name,
        description: db.description || "",
        source: "igloobot-db",
        filePath: "",
        baseDir: "",
        skillKey: db.name,
        emoji: (meta.emoji as string) ?? undefined,
        always: false,
        disabled: !db.isActive,
        blockedByAllowlist: false,
        eligible: db.isActive,
        requirements: { bins: [], env: [], config: [], os: [] },
        missing: { bins: [], env: [], config: [], os: [] },
        configChecks: [],
        install: [],
        globalActive: db.isActive,
      });
    }
  }

  return merged;
}
